import { Card } from "./ui/card"
import { useEffect, useState } from "react"
import { CustomToggle } from "./CustomToggle"
import { AuthModel } from "../models/auth.model"
import { AuthService } from "../services/auth.service"
import { formatTimeDifference, getCurrentDate } from "../lib/utils"
import { ChartBar, RefreshCcw } from "lucide-react"
import { LocationHistoryChart } from "./LocationHistoryChart"
import { DatePicker } from "./DatePicker"

export interface LocationReportProps {
    id: number,
    globalDateOffset: number,
    timezone: string,
    changeDate: (offset: number) => void;
}

// Strip layout differs between the two chart modes; the get_door_edges API
// returns the right numbers per mode (raw uses a 200px left labels column):
//   raw=false -> image 1468 wide, strip starts x=12
//   raw=true  -> image 1640 wide, strip starts x=200
// Lines are positioned by percent-of-natural-image so the overlay tracks any
// CSS display width with no clientWidth/naturalWidth measurement.
interface DoorEdges {
    open_minutes: number[]
    close_minutes: number[]
    strip_left_px: number
    strip_width_px: number
    image_natural_width: number
}

export function LocationReport(props: LocationReportProps) {
    const [dateOffset, setDateOffset] = useState<number>(0)
    const [raw, setRaw] = useState<boolean>(false)
    const [showDoors, setShowDoors] = useState<boolean>(false)
    const [doorEdges, setDoorEdges] = useState<DoorEdges | null>(null)
    const [loadedOn, setLoadedOn] = useState<string>(new Date().toISOString())

    const [, setCurrentTime] = useState(new Date())

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date())
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        setLoadedOn(new Date().toISOString())
    }, [raw, dateOffset, props.globalDateOffset])

    // Door edges fetch — only when Show Doors is on AND in Day view. Refetches
    // when `raw` flips because the strip layout (and image width) differ.
    useEffect(() => {
        if (!showDoors || dateOffset !== 0) { setDoorEdges(null); return }
        let cancelled = false
        const auth: AuthModel = AuthService.getAuth()
        const date = getCurrentDate(props.globalDateOffset, props.timezone)
        const url = `${process.env.REACT_APP_API_BASE}/function/well-api/api?name=get_door_edges&deployment_id=${props.id}&date=${date}&raw=${raw}&user_name=${auth.username}&token=${auth.token}`
        fetch(url)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('door-edges failed')))
            .then(data => {
                if (cancelled) return
                setDoorEdges({
                    open_minutes: data.open_minutes || [],
                    close_minutes: data.close_minutes || [],
                    strip_left_px: data.strip_left_px ?? 12,
                    strip_width_px: data.strip_width_px || 1440,
                    image_natural_width: data.image_natural_width || 1468,
                })
            })
            .catch(() => { if (!cancelled) setDoorEdges(null) })
        return () => { cancelled = true }
    }, [showDoors, raw, dateOffset, props.id, props.globalDateOffset, props.timezone, loadedOn])

    function getLocationMap(requiredDateOffset: number): string {
        const auth: AuthModel = AuthService.getAuth()
        const date = getCurrentDate(props.globalDateOffset, props.timezone)
        const offset = getCurrentDate(requiredDateOffset + props.globalDateOffset, props.timezone)
        const base = `${process.env.REACT_APP_API_BASE}/function/well-api/api?name=get_full_location_map`
        return `${base}&deployment_id=${props.id}&date=${date}&re_create=false&bw=false&motion=false&scale_global=false&map_type=7&user_name=${auth.username}&fast=true&filter=5&token=${auth.token}&to_date=${offset}&raw=${raw}&now=${loadedOn}`
    }

    function toggleRawLocationMap(checked: boolean) {
        setRaw(checked)
        setDateOffset(dateOffset)
    }

    function generateAcitvityReportButtonClassNames(active: boolean = false) {
        return active ? 'px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg' : 'px-4 py-2 text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
    }


    return (
        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="location_report">
            <div className="space-y-2">
                <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <ChartBar className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32}/> Location Report
                    </h3>
                    <DatePicker value={props.globalDateOffset} onChange={props.changeDate} timezone={props.timezone} />
                </div>
                <div className="text-sm flex justify-center items-center gap-2">
                    <p>
                        {(() => {
                            const calculated = formatTimeDifference(loadedOn)
                            return `Last report ${calculated.minutes} min ago`
                        })()}
                    </p>
                    <RefreshCcw
                        className="cursor-pointer"
                        onClick={() => {
                            setDateOffset(dateOffset - 1)
                            setTimeout(() => setDateOffset(dateOffset), 0)
                        }}
                    />
                </div>

                <div className="flex justify-center gap-2 mt-2">
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === 0)} type="button" onClick={() => setDateOffset(0)}>
                        Day
                    </button>
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === -7)} type="button" onClick={() => setDateOffset(-7)}>
                        Week
                    </button>
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === -30)} type="button" onClick={() => setDateOffset(-30)}>
                        1Mo
                    </button>
                    <button className={generateAcitvityReportButtonClassNames(dateOffset === -180)} type="button" onClick={() => setDateOffset(-180)}>
                        6Mo
                    </button>
                </div>
                {dateOffset === 0 &&
                    <div className="flex justify-center gap-6 items-center mt-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">Show raw</span>
                            <CustomToggle checked={raw} onCheckedChange={(checked) => toggleRawLocationMap(checked)} />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">Show Doors</span>
                            <CustomToggle checked={showDoors} onCheckedChange={(checked) => setShowDoors(checked)} />
                        </div>
                    </div>
                }
                <div className="mt-4 relative inline-block w-full">
                    <img
                        src={getLocationMap(dateOffset)}
                        alt="Activity Report"
                        className="w-full rounded-lg block"
                    />
                    {showDoors && dateOffset === 0 && doorEdges && (
                        <>
                            {/* Percentage positioning: x = (strip_left_px + minute) / image_natural_width.
                                strip_left_px and image_natural_width come from the API and differ
                                between raw (200 / 1640) and not-raw (12 / 1468) charts. 3 CSS px
                                wide, full image height. Close painted first so a same-minute
                                open+close shows red on top. */}
                            {doorEdges.close_minutes.map(m => (
                                <div key={`c${m}`} style={{
                                    position:'absolute',
                                    left: `${((doorEdges.strip_left_px + m) / doorEdges.image_natural_width) * 100}%`,
                                    top: 0,
                                    bottom: 0,
                                    width: 3,
                                    marginLeft: -1.5,
                                    background: 'blue',
                                    pointerEvents: 'none',
                                    zIndex: 2,
                                }}/>
                            ))}
                            {doorEdges.open_minutes.map(m => (
                                <div key={`o${m}`} style={{
                                    position:'absolute',
                                    left: `${((doorEdges.strip_left_px + m) / doorEdges.image_natural_width) * 100}%`,
                                    top: 0,
                                    bottom: 0,
                                    width: 3,
                                    marginLeft: -1.5,
                                    background: 'red',
                                    pointerEvents: 'none',
                                    zIndex: 2,
                                }}/>
                            ))}
                        </>
                    )}
                </div>
                <LocationHistoryChart
                    deploymentId={props.id}
                    date={getCurrentDate(props.globalDateOffset, props.timezone)}
                    toDate={getCurrentDate(dateOffset + props.globalDateOffset, props.timezone)}
                />
            </div>
        </Card>
    )
}
